import { supabase } from '../lib/supabaseClient';

export interface Employee {
  id: string;
  name: string;
  email?: string;
  pin: string;
  is_admin: boolean;
  photo_url?: string;
  created_at: string;
}

export interface CreateEmployeeData {
  name: string;
  email?: string;
  is_admin?: boolean;
  photo_url?: string;
}

/**
 * Get all employees
 */
export async function getEmployees(): Promise<{ data: Employee[]; error: any }> {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Employee Service] Error fetching employees:', error);
  }

  return { data: data || [], error };
}

/**
 * Get employee by ID
 */
export async function getEmployeeById(id: string): Promise<{ data: Employee | null; error: any }> {
  const { data, error } = await supabase
    .from('employees')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('[Employee Service] Error fetching employee:', error);
  }

  return { data, error };
}

/**
 * Create a new employee with auto-generated PIN
 */
export async function createEmployee(
  employeeData: CreateEmployeeData
): Promise<{ data: Employee | null; error: any; pin?: string }> {
  // Generate a random 4-digit PIN
  const pin = Math.floor(1000 + Math.random() * 9000).toString();

  const { data, error } = await supabase
    .from('employees')
    .insert({
      name: employeeData.name,
      email: employeeData.email || null,
      pin,
      is_admin: employeeData.is_admin || false,
      photo_url: employeeData.photo_url || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[Employee Service] Error creating employee:', error);
    return { data: null, error };
  }

  console.log('[Employee Service] Created employee with PIN:', pin);
  return { data, error: null, pin };
}

/**
 * Update employee
 */
export async function updateEmployee(
  id: string,
  updates: Partial<Omit<Employee, 'id' | 'created_at'>>
): Promise<{ data: Employee | null; error: any }> {
  const { data, error } = await supabase
    .from('employees')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[Employee Service] Error updating employee:', error);
  }

  return { data, error };
}

/**
 * Reset employee PIN (generates new random PIN)
 */
export async function resetEmployeePIN(id: string): Promise<{ data: Employee | null; error: any; pin?: string }> {
  const pin = Math.floor(1000 + Math.random() * 9000).toString();

  const { data, error } = await supabase
    .from('employees')
    .update({
      pin,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[Employee Service] Error resetting PIN:', error);
    return { data: null, error };
  }

  console.log('[Employee Service] Reset PIN for employee:', id);
  return { data, error: null, pin };
}

/**
 * Delete employee
 */
export async function deleteEmployee(id: string): Promise<{ error: any }> {
  const { error } = await supabase
    .from('employees')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[Employee Service] Error deleting employee:', error);
  }

  return { error };
}

/**
 * Generate QR code data for an employee
 * Format: { v: 1, id: employee_id, ts: timestamp }
 */
export function generateQRData(employeeId: string): string {
  const payload = {
    v: 1,
    id: employeeId,
    ts: Date.now(),
  };
  return btoa(JSON.stringify(payload));
}

/**
 * Validate and parse QR code data
 */
export function validateQRData(qrData: string): { valid: boolean; employeeId?: string } {
  try {
    const payload = JSON.parse(atob(qrData));

    if (!payload.id || payload.v !== 1) {
      return { valid: false };
    }

    // QR codes don't expire in this simple system
    // (optional: add expiration check here if needed)

    return { valid: true, employeeId: payload.id };
  } catch {
    return { valid: false };
  }
}

/**
 * Get all employees with their comp balances
 */
export async function getEmployeesWithBalances(): Promise<{ data: Array<Employee & { drinks_available: number; food_available: number }>; error: any }> {
  const { data: employees, error: employeesError } = await getEmployees();

  if (employeesError) {
    return { data: [], error: employeesError };
  }

  // Get balances for each employee
  const employeesWithBalances = await Promise.all(
    (employees || []).map(async (employee) => {
      const { data: balance } = await supabase
        .rpc('get_employee_comps', { p_employee_id: employee.id });

      return {
        ...employee,
        drinks_available: balance?.[0]?.drinks_available || 0,
        food_available: balance?.[0]?.food_available || 0,
      };
    })
  );

  return { data: employeesWithBalances, error: null };
}
